import { getApiBaseUrl } from './api';

// Helper to get auth token from localStorage (same as wallet service)
const getAuthToken = (): string | null => {
  try {
    const sessionData = localStorage.getItem('booking_service_auth');
    if (sessionData) {
      const session = JSON.parse(sessionData);
      // Check if session is still valid (not expired)
      if (session.expiresAt && Date.now() <= session.expiresAt) {
        return session.token || null;
      }
    }
    return null;
  } catch (error) {
    console.error('Error getting auth token:', error);
    return null;
  }
};

export interface Member {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
  phone?: string;
  customerType: string;
  customerTypeId: string;
  membershipNumber?: string;
  isActive: boolean;
}

export interface MemberSearchResult {
  members: Member[];
  total: number;
}

export const memberDirectoryService = {
  // Search for members by name
  async searchMembers(query: string, limit: number = 10, clubNumber?: number): Promise<MemberSearchResult> {
    try {
      console.log('🔍 memberDirectoryService: Searching for members with query:', query, 'clubNumber:', clubNumber);
      
      const token = getAuthToken();
      console.log('🔍 memberDirectoryService: Token retrieved:', token ? `Token exists (length: ${token.length})` : 'No token found');
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        console.log('✅ memberDirectoryService: Authorization header added to request');
      } else {
        console.warn('⚠️ memberDirectoryService: No token available for member search request');
      }
      
      // Build query string
      const queryParams = new URLSearchParams({
        query: query,
        limit: limit.toString()
      });
      
      // Add club context if provided (needed for multi-tenant)
      if (clubNumber) {
        queryParams.append('clubNumber', clubNumber.toString());
      }
      
      const apiUrl = `${getApiBaseUrl()}/customers?${queryParams.toString()}`;
      console.log('🔍 memberDirectoryService: Making request to:', apiUrl);
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ memberDirectoryService: API error:', response.status, errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('🔍 memberDirectoryService: API response received, customers count:', data.customers?.length || 0);
      
      // Filter for members only - check customerType.category === 'member'
      const allCustomers = data.customers || [];
      console.log('🔍 memberDirectoryService: All customers found:', allCustomers.length);
      
      const members = allCustomers
        .filter((customer: any) => {
          // Check if customer type category is 'member'
          const customerType = customer.customerType;
          const isMember = typeof customerType === 'object' 
            ? customerType?.category === 'member'
            : false; // If it's just an ID, we need to check differently
          
          console.log('🔍 memberDirectoryService: Customer', customer.firstName, customer.lastName, 
            'isMember:', isMember, 
            'customerType:', typeof customerType === 'object' ? customerType?.name : customerType,
            'category:', typeof customerType === 'object' ? customerType?.category : 'N/A');
          
          return isMember;
        })
        .slice(0, limit)
        .map((customer: any) => ({
          id: customer.id,
          firstName: customer.firstName,
          lastName: customer.lastName,
          email: customer.email,
          phone: customer.phone,
          customerType: typeof customer.customerType === 'object' ? customer.customerType?.name : customer.customerType,
          customerTypeId: typeof customer.customerType === 'object' ? customer.customerType?.id : customer.customerType,
          membershipNumber: customer.membershipId || customer.membershipNumber,
          isActive: customer.status === 'active'
        }));
      
      console.log('✅ memberDirectoryService: Filtered members:', members.length);
      
      return {
        members,
        total: members.length
      };
    } catch (error) {
      console.error('❌ Error searching members:', error);
      return {
        members: [],
        total: 0
      };
    }
  },

  // Get member by ID
  async getMemberById(memberId: string): Promise<Member | null> {
    try {
      const token = getAuthToken();
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${getApiBaseUrl()}/customers/${memberId}`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      const customer = data;
      return {
        id: customer.id,
        firstName: customer.firstName,
        lastName: customer.lastName,
        email: customer.email,
        phone: customer.phone,
        customerType: typeof customer.customerType === 'object' ? customer.customerType?.name : customer.customerType,
        customerTypeId: typeof customer.customerType === 'object' ? customer.customerType?.id : customer.customerType,
        membershipNumber: customer.membershipId || customer.membershipNumber,
        isActive: customer.status === 'active'
      };
    } catch (error) {
      console.error('Error fetching member by ID:', error);
      return null;
    }
  },

  // Get all members (for dropdown/autocomplete)
  async getAllMembers(limit: number = 100, clubNumber?: number): Promise<Member[]> {
    try {
      const token = getAuthToken();
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const queryParams = new URLSearchParams({
        limit: limit.toString()
      });
      
      if (clubNumber) {
        queryParams.append('clubNumber', clubNumber.toString());
      }
      
      const response = await fetch(`${getApiBaseUrl()}/customers?${queryParams.toString()}`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Filter for members only using the same logic as searchMembers
      const allCustomers = data.customers || [];
      const members = allCustomers
        .filter((customer: any) => {
          const customerType = customer.customerType;
          return typeof customerType === 'object' && customerType?.category === 'member';
        })
        .map((customer: any) => ({
          id: customer.id,
          firstName: customer.firstName,
          lastName: customer.lastName,
          email: customer.email,
          phone: customer.phone,
          customerType: typeof customer.customerType === 'object' ? customer.customerType?.name : customer.customerType,
          customerTypeId: typeof customer.customerType === 'object' ? customer.customerType?.id : customer.customerType,
          membershipNumber: customer.membershipId || customer.membershipNumber,
          isActive: customer.status === 'active'
        }));
      
      return members;
    } catch (error) {
      console.error('Error fetching all members:', error);
      return [];
    }
  }
};
